import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  TrendingUp, Search, X, Loader2, CheckCircle, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight, Package, Building2, Plus, Send, AlertCircle,
  FileText, PlusCircle,
} from 'lucide-react';

const PRIMARY = '#0099FF';
const ITEMS_PER_PAGE = 15;

const ESTADO_BADGES = {
  pendiente:  { label: 'Pendiente',  bg: 'bg-amber-100',  text: 'text-amber-800' },
  aprobada:   { label: 'Aprobada',   bg: 'bg-green-100',  text: 'text-green-700' },
  rechazada:  { label: 'Rechazada',  bg: 'bg-red-100',    text: 'text-red-700' },
};

const getEstadoBadge = (estado) =>
  ESTADO_BADGES[estado] || { label: estado || '—', bg: 'bg-gray-100', text: 'text-gray-600' };

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const ComercialOfertas = () => {
  const { token, API_URL, user } = useAuth();

  // ── Tab state ──
  const [activeTab, setActiveTab] = useState('mis-ofertas');

  // ── Create offer state ──
  const [step, setStep] = useState(1); // 1=products, 2=clients, 3=prices+review
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [priceMatrix, setPriceMatrix] = useState({}); // { `${productId}-${clientId}`: price }
  const [previewData, setPreviewData] = useState({ products: [], current_prices: {} });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [comentarios, setComentarios] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Product search
  const [prodSearch, setProdSearch] = useState('');
  const [prodResults, setProdResults] = useState([]);
  const [loadingProds, setLoadingProds] = useState(false);
  const prodTimer = useRef(null);

  // Client search
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const clientTimer = useRef(null);

  // ── Past offers state ──
  const [offers, setOffers] = useState([]);
  const [totalOffers, setTotalOffers] = useState(0);
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [offersPage, setOffersPage] = useState(1);
  const [expandedOffer, setExpandedOffer] = useState(null);
  const [expandedDetails, setExpandedDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const totalPages = Math.ceil(totalOffers / ITEMS_PER_PAGE);

  // ── Product search ──
  const searchProducts = useCallback(async (q) => {
    if (!q || q.length < 2) { setProdResults([]); return; }
    setLoadingProds(true);
    try {
      const res = await fetch(`${API_URL}/products?search=${encodeURIComponent(q)}&limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProdResults(data.products || []);
      }
    } catch (err) {
      console.error('Error searching products:', err);
    } finally {
      setLoadingProds(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    if (prodTimer.current) clearTimeout(prodTimer.current);
    prodTimer.current = setTimeout(() => searchProducts(prodSearch), 300);
    return () => clearTimeout(prodTimer.current);
  }, [prodSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const addProduct = (product) => {
    if (selectedProducts.find(p => p.id === product.id)) return;
    setSelectedProducts(prev => [...prev, product]);
    setProdSearch('');
    setProdResults([]);
  };

  const removeProduct = (productId) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
    setPriceMatrix(prev => {
      const n = { ...prev };
      Object.keys(n).forEach(key => {
        if (key.startsWith(`${productId}-`)) delete n[key];
      });
      return n;
    });
  };

  // ── Client search (only MY clients via /api/clients) ──
  const searchClients = useCallback(async (q) => {
    if (!q || q.length < 2) { setClientResults([]); return; }
    setLoadingClients(true);
    try {
      const res = await fetch(`${API_URL}/clients/search?q=${encodeURIComponent(q)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setClientResults(data.clients || []);
      }
    } catch (err) {
      console.error('Error searching clients:', err);
    } finally {
      setLoadingClients(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    if (clientTimer.current) clearTimeout(clientTimer.current);
    clientTimer.current = setTimeout(() => searchClients(clientSearch), 300);
    return () => clearTimeout(clientTimer.current);
  }, [clientSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const addClient = (client) => {
    if (selectedClients.find(c => c.id === client.id)) return;
    setSelectedClients(prev => [...prev, client]);
    setClientSearch('');
    setClientResults([]);
  };

  const removeClient = (clientId) => {
    setSelectedClients(prev => prev.filter(c => c.id !== clientId));
    setPriceMatrix(prev => {
      const n = { ...prev };
      Object.keys(n).forEach(key => {
        if (key.endsWith(`-${clientId}`)) delete n[key];
      });
      return n;
    });
  };

  // Load price preview when entering step 3
  useEffect(() => {
    if (step !== 3 || selectedProducts.length === 0 || selectedClients.length === 0) return;
    const loadPreview = async () => {
      setLoadingPreview(true);
      try {
        const res = await fetch(`${API_URL}/ofertas/preview-prices`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            products: selectedProducts.map(p => p.id),
            clients: selectedClients.map(c => c.id),
          }),
        });
        if (res.ok) {
          const data = await res.json();
          setPreviewData(data);
        }
      } catch (err) {
        console.error('Error loading preview:', err);
      } finally {
        setLoadingPreview(false);
      }
    };
    loadPreview();
  }, [step, selectedProducts, selectedClients, API_URL, token]);

  const handleCellPriceChange = (productId, clientId, value) => {
    setPriceMatrix(prev => ({ ...prev, [`${productId}-${clientId}`]: value }));
  };

  // ── Submit offer ──
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const items = [];
      selectedProducts.forEach(product => {
        selectedClients.forEach(client => {
          const key = `${product.id}-${client.id}`;
          const precio = priceMatrix[key] || 0;
          if (Number(precio) > 0) {
            items.push({
              cliente_id: client.id,
              producto_id: product.id,
              precio_propuesto: Number(precio),
            });
          }
        });
      });

      if (items.length === 0) {
        setSubmitError('Debes establecer al menos un precio mayor a 0');
        setSubmitting(false);
        return;
      }

      const res = await fetch(`${API_URL}/ofertas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ items, comentarios }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Error al crear la oferta');
      }

      setSubmitSuccess(true);
      // Reset form
      setSelectedProducts([]);
      setSelectedClients([]);
      setPriceMatrix({});
      setComentarios('');
      setStep(1);
      // Refresh offers list
      fetchOffers();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Fetch past offers ──
  const fetchOffers = useCallback(async () => {
    setLoadingOffers(true);
    try {
      const params = new URLSearchParams({ page: String(offersPage), limit: String(ITEMS_PER_PAGE) });
      const res = await fetch(`${API_URL}/ofertas?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar ofertas');
      const data = await res.json();
      setOffers(data.ofertas || []);
      setTotalOffers(data.total || 0);
    } catch (err) {
      console.error('Error fetching offers:', err);
    } finally {
      setLoadingOffers(false);
    }
  }, [API_URL, token, offersPage]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const toggleOfferExpand = async (offerId) => {
    if (expandedOffer === offerId) {
      setExpandedOffer(null);
      setExpandedDetails([]);
      return;
    }
    setExpandedOffer(offerId);
    setLoadingDetails(true);
    try {
      const res = await fetch(`${API_URL}/ofertas/${offerId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar detalle');
      const data = await res.json();
      setExpandedDetails(data.detalle || data.oferta?.detalles || []);
    } catch (err) {
      console.error('Error fetching offer detail:', err);
      setExpandedDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  // ── Validation ──
  const canGoToStep2 = selectedProducts.length > 0;
  const canSubmit = Object.values(priceMatrix).some(v => Number(v) > 0);

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <div className="h-full overflow-y-auto p-6">
      {/* Page Header */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
          <TrendingUp className="w-6 h-6" style={{ color: PRIMARY }} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Ofertas</h1>
          <p className="text-sm text-gray-500">Crea y gestiona tus ofertas comerciales</p>
        </div>
      </div>

      {/* Success message */}
      {submitSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-green-800">Oferta enviada correctamente</p>
            <p className="text-xs text-green-600 mt-0.5">El administrador la revisará próximamente.</p>
          </div>
          <button onClick={() => setSubmitSuccess(false)} className="ml-auto text-green-400 hover:text-green-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* ── TABS ── */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('mis-ofertas')}
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'mis-ofertas'
              ? 'border-[#0099FF] text-[#0099FF]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileText size={16} /> Mis Ofertas
        </button>
        <button
          onClick={() => setActiveTab('nueva-oferta')}
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'nueva-oferta'
              ? 'border-[#0099FF] text-[#0099FF]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <PlusCircle size={16} /> Nueva Oferta
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* TAB: MIS OFERTAS                                                  */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'mis-ofertas' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Mis Ofertas</h2>
            <p className="text-sm text-gray-500">{totalOffers} oferta(s) en total</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Oferta</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Productos</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingOffers ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{ borderColor: PRIMARY }} />
                    </td>
                  </tr>
                ) : offers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No tienes ofertas todavía</p>
                    </td>
                  </tr>
                ) : (
                  offers.map(offer => {
                    const badge = getEstadoBadge(offer.estado);
                    const isExpanded = expandedOffer === offer.id;
                    return (
                      <React.Fragment key={offer.id}>
                        <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleOfferExpand(offer.id)}>
                          <td className="px-6 py-4">
                            {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">#{offer.id}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{formatDate(offer.fecha_creacion)}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 text-right">{offer.total_productos || offer.num_productos || '—'}</td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan="5" className="px-6 py-4 bg-gray-50">
                              {loadingDetails ? (
                                <div className="flex items-center justify-center py-4">
                                  <Loader2 size={20} className="animate-spin text-gray-400" />
                                  <span className="ml-2 text-sm text-gray-500">Cargando detalle...</span>
                                </div>
                              ) : expandedDetails.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-2">Sin detalles</p>
                              ) : (
                                <table className="min-w-full text-sm">
                                  <thead>
                                    <tr className="text-xs text-gray-500 uppercase">
                                      <th className="pb-2 text-left font-medium">Cliente</th>
                                      <th className="pb-2 text-left font-medium">Producto</th>
                                      <th className="pb-2 text-right font-medium">Precio Propuesto</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {expandedDetails.map((item, idx) => (
                                      <tr key={idx}>
                                        <td className="py-2 text-gray-900">{item.cliente_nombre || item.razon_social || '—'}</td>
                                        <td className="py-2 text-gray-600">{item.producto_nombre || item.nombre || '—'}</td>
                                        <td className="py-2 text-gray-900 text-right font-medium">{formatCurrency(item.precio_propuesto)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                              {offer.estado === 'rechazada' && offer.motivo_rechazo && (
                                <div className="mt-3 p-3 bg-red-50 rounded-lg">
                                  <p className="text-xs font-medium text-red-600">Motivo de rechazo:</p>
                                  <p className="text-sm text-red-700">{offer.motivo_rechazo}</p>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-500">
                {((offersPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(offersPage * ITEMS_PER_PAGE, totalOffers)} de {totalOffers}
              </p>
              <div className="flex items-center gap-2">
                <button onClick={() => setOffersPage(p => Math.max(1, p - 1))} disabled={offersPage === 1}
                  className="p-1.5 rounded-md border border-gray-300 hover:bg-white disabled:opacity-40 transition-colors">
                  <ChevronLeft size={16} />
                </button>
                <span className="text-sm text-gray-700 font-medium px-2">{offersPage} / {totalPages}</span>
                <button onClick={() => setOffersPage(p => Math.min(totalPages, p + 1))} disabled={offersPage === totalPages}
                  className="p-1.5 rounded-md border border-gray-300 hover:bg-white disabled:opacity-40 transition-colors">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* TAB: NUEVA OFERTA                                                 */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'nueva-oferta' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Nueva Oferta</h2>
            {/* Step indicator */}
            <div className="flex items-center gap-2 mt-3 text-sm">
              {[
                { n: 1, label: 'Productos' },
                { n: 2, label: 'Clientes' },
                { n: 3, label: 'Precios y Envío' },
              ].map((s, idx) => (
                <React.Fragment key={s.n}>
                  {idx > 0 && <span className="text-gray-300">—</span>}
                  <button
                    onClick={() => {
                      if (s.n === 1) setStep(1);
                      if (s.n === 2 && canGoToStep2) setStep(2);
                      if (s.n === 3 && canGoToStep2 && selectedClients.length > 0) setStep(3);
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      step === s.n
                        ? 'bg-[#0099FF] text-white'
                        : step > s.n ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <span>{step > s.n ? '\u2713' : s.n}</span>
                    {s.label}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* ── STEP 1: Select Products ── */}
            {step === 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Buscar y agregar productos</label>
                <div className="relative max-w-lg mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input type="text" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)}
                    placeholder="Escribe al menos 2 caracteres..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': PRIMARY }} />
                  {loadingProds && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />}
                  {prodResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {prodResults.map(product => {
                        const added = selectedProducts.find(p => p.id === product.id);
                        return (
                          <button key={product.id} onClick={() => !added && addProduct(product)} disabled={!!added}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center justify-between ${added ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}>
                            <div>
                              <span className="font-medium text-gray-900">{product.nombre}</span>
                              {product.raiz && <span className="ml-2 text-xs text-gray-500 font-mono">{product.raiz}</span>}
                            </div>
                            {added ? <span className="text-xs text-green-600">Agregado</span> : <Plus size={14} className="text-gray-400" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {selectedProducts.length > 0 ? (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referencia</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-16"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedProducts.map(product => (
                          <tr key={product.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{product.nombre}</td>
                            <td className="px-4 py-3 text-sm text-gray-500 font-mono">{product.raiz || '—'}</td>
                            <td className="px-4 py-3 text-right">
                              <button onClick={() => removeProduct(product.id)} className="text-red-400 hover:text-red-600">
                                <X size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <Package className="w-10 h-10 mx-auto mb-2" />
                    <p className="text-sm">Busca y agrega productos para tu oferta</p>
                  </div>
                )}
                <div className="mt-4 flex justify-end">
                  <button onClick={() => setStep(2)} disabled={!canGoToStep2}
                    className="px-4 py-2.5 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: PRIMARY }}>
                    Siguiente: Clientes
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 2: Select Clients (MIS CLIENTES only) ── */}
            {step === 2 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Buscar entre mis clientes</label>
                <div className="relative max-w-lg mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input type="text" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Buscar por razón social..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2" />
                  {loadingClients && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />}
                  {clientResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {clientResults.map(client => {
                        const added = selectedClients.find(c => c.id === client.id);
                        return (
                          <button key={client.id} onClick={() => !added && addClient(client)} disabled={!!added}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center justify-between ${added ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}>
                            <span className="font-medium text-gray-900">{client.razon_social}</span>
                            {added ? <span className="text-xs text-green-600">Agregado</span> : <Plus size={14} className="text-gray-400" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {selectedClients.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedClients.map(client => (
                      <span key={client.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                        <Building2 size={12} /> {client.razon_social}
                        <button onClick={() => removeClient(client.id)} className="ml-0.5 text-blue-400 hover:text-blue-700">
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {selectedClients.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <Building2 className="w-10 h-10 mx-auto mb-2" />
                    <p className="text-sm">Busca y agrega tus clientes</p>
                  </div>
                )}
                <div className="mt-4 flex justify-between">
                  <button onClick={() => setStep(1)} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                    Volver
                  </button>
                  <button onClick={() => setStep(3)} disabled={selectedClients.length === 0}
                    className="px-4 py-2.5 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: PRIMARY }}>
                    Siguiente: Precios
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 3: Price per client + Submit ── */}
            {step === 3 && (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Establece el precio para cada producto y cliente.
                </p>

                {submitError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="flex-shrink-0" /> {submitError}
                  </div>
                )}

                {loadingPreview && (
                  <div className="flex items-center justify-center py-4 mb-2 text-gray-400">
                    <Loader2 size={16} className="animate-spin mr-2" /> Cargando precios actuales...
                  </div>
                )}

                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 z-10 min-w-[200px] border-r border-gray-200">
                          Producto
                        </th>
                        <th rowSpan="2" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase min-w-[100px] border-r border-gray-200">
                          P. Coste
                        </th>
                        {selectedClients.map(client => (
                          <th key={client.id} colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase border-l border-gray-200" title={client.razon_social}>
                            <div className="truncate max-w-[200px] mx-auto">{client.razon_social}</div>
                          </th>
                        ))}
                      </tr>
                      <tr>
                        {selectedClients.map(client => (
                          <React.Fragment key={client.id}>
                            <th className="px-2 py-2 text-right text-[10px] font-medium text-gray-500 uppercase border-l border-gray-200 min-w-[90px]">Actual</th>
                            <th className="px-2 py-2 text-center text-[10px] font-medium text-gray-500 uppercase min-w-[100px]">Nuevo</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedProducts.map(product => {
                        const previewProduct = previewData.products.find(p => p.id === product.id);
                        const coste = previewProduct?.precio_coste;
                        return (
                          <tr key={product.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm sticky left-0 bg-white z-10 border-r border-gray-200">
                              <div className="font-medium text-gray-900">{product.nombre}</div>
                              {product.raiz && <div className="text-xs text-gray-400 font-mono">{product.raiz}</div>}
                            </td>
                            <td className="px-3 py-3 text-right text-sm text-gray-600 font-mono border-r border-gray-200">
                              {coste != null ? `${Number(coste).toFixed(2)} €` : '—'}
                            </td>
                            {selectedClients.map(client => {
                              const key = `${product.id}-${client.id}`;
                              const currentPrice = previewData.current_prices?.[key];
                              return (
                                <React.Fragment key={client.id}>
                                  <td className="px-2 py-3 text-right text-sm text-gray-500 font-mono border-l border-gray-200">
                                    {currentPrice != null ? `${currentPrice.toFixed(2)} €` : <span className="text-gray-300">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center">
                                    <input
                                      type="number" step="0.01" min="0" placeholder="0.00"
                                      value={priceMatrix[key] || ''}
                                      onChange={(e) => handleCellPriceChange(product.id, client.id, e.target.value)}
                                      className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30"
                                    />
                                  </td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Comments */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comentarios (opcional)</label>
                  <textarea value={comentarios} onChange={(e) => setComentarios(e.target.value)} rows={2}
                    placeholder="Notas adicionales sobre la oferta..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 resize-none" />
                </div>

                <div className="mt-6 flex justify-between">
                  <button onClick={() => setStep(2)} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                    Volver
                  </button>
                  <button onClick={handleSubmit} disabled={submitting || !canSubmit}
                    className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: PRIMARY }}>
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    Enviar Oferta
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComercialOfertas;
